import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import GamificationService from '../services/GamificationService';
import DisCard from './design-system/DisCard';
import DisButton from './design-system/DisButton';
import { trackEvent, EVENTS } from '../services/analytics';

const StarConversionSection = () => {
  const { api } = useAuth();
  const [starsData, setStarsData] = useState(null);
  const [converting, setConverting] = useState(false);
  const [convertAnim, setConvertAnim] = useState(null); // 'question' or 'story'

  useEffect(() => {
    const load = async () => {
      if (!api) return;
      const svc = GamificationService.getInstance();
      svc.setApi(api);
      const data = await svc.getStars();
      setStarsData(data);
    };
    load();
  }, [api]);

  const handleConvert = async (type) => {
    if (converting) return;
    setConverting(true);
    setConvertAnim(type);
    try {
      const svc = GamificationService.getInstance();
      svc.setApi(api);
      const result = await svc.convertStars(type === 'question' ? 'question_credit' : 'story_credit');
      trackEvent(EVENTS.STARS_SPENT, { reward: type, cost: result.cost });
      // Refresh data
      const updated = await svc.getStars();
      setStarsData(updated);
      // Update header
      window.dispatchEvent(new CustomEvent('stars-earned', {
        detail: { amount: 0, newBalance: result.new_balance }
      }));
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur de conversion');
    } finally {
      setConverting(false);
      setTimeout(() => setConvertAnim(null), 1500);
    }
  };

  if (!starsData || starsData.balance === 0) return null;

  const { balance, is_premium, conversions } = starsData;
  const canQuestion = conversions.can_convert_questions;
  const canStory = conversions.can_convert_stories;
  const questionCost = conversions.question_cost;
  const storyCost = conversions.story_cost;
  const storyOriginal = conversions.story_cost_original;

  return (
    <DisCard className="p-4 mb-4 bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200" data-testid="star-conversion-section">
      <h3 className="text-base font-bold text-amber-800 mb-3" style={{ fontFamily: "'Caveat', cursive" }}>
        &#11088; Mes étoiles
      </h3>

      <div className="flex items-center justify-center gap-2 mb-4">
        <span className="text-3xl font-bold text-amber-600" data-testid="star-conversion-balance">{balance}</span>
        <span className="text-amber-500 text-sm">étoiles disponibles</span>
      </div>

      <div className="space-y-3">
        {/* Question credit conversion */}
        {!is_premium && (
          <div className={`flex items-center justify-between p-3 rounded-xl border transition-all ${
            canQuestion && balance >= questionCost
              ? 'bg-white border-blue-200'
              : 'bg-gray-50 border-gray-200 opacity-60'
          } ${convertAnim === 'question' ? 'animate-pulse ring-2 ring-blue-400' : ''}`}>
            <div>
              <p className="text-sm font-medium text-gray-800">+1 question bonus</p>
              <p className="text-xs text-gray-400">
                {conversions.max_questions_per_month - conversions.questions_converted_this_month > 0
                  ? `${conversions.max_questions_per_month - conversions.questions_converted_this_month} question${conversions.max_questions_per_month - conversions.questions_converted_this_month > 1 ? 's' : ''} bonus encore disponible${conversions.max_questions_per_month - conversions.questions_converted_this_month > 1 ? 's' : ''} ce mois`
                  : 'Plus de question bonus disponible ce mois'
                }
              </p>
            </div>
            <DisButton
              onClick={() => handleConvert('question')}
              disabled={!canQuestion || balance < questionCost || converting}
              className="text-xs px-3 py-1.5 bg-blue-500 text-white"
              data-testid="convert-question-btn"
            >
              {questionCost} &#11088;
            </DisButton>
          </div>
        )}

        {/* Story credit conversion */}
        <div className={`flex items-center justify-between p-3 rounded-xl border transition-all ${
          canStory && balance >= storyCost
            ? 'bg-white border-purple-200'
            : 'bg-gray-50 border-gray-200 opacity-60'
        } ${convertAnim === 'story' ? 'animate-pulse ring-2 ring-purple-400' : ''}`}>
          <div>
            <p className="text-sm font-medium text-gray-800">+1 histoire bonus</p>
            <p className="text-xs text-gray-400">
              {conversions.max_stories_per_month - conversions.stories_converted_this_month > 0
                ? `${conversions.max_stories_per_month - conversions.stories_converted_this_month} histoire bonus encore disponible ce mois`
                : "Plus d'histoire bonus disponible ce mois"
              }
            </p>
            {is_premium && storyOriginal && (
              <p className="text-[10px] text-amber-600 mt-0.5">
                <span className="line-through text-gray-400">{storyOriginal}</span>{' '}
                <span className="font-bold text-amber-600">{storyCost}</span>{' '}
                — remise abonnés
              </p>
            )}
          </div>
          <DisButton
            onClick={() => handleConvert('story')}
            disabled={!canStory || balance < storyCost || converting}
            className="text-xs px-3 py-1.5 bg-purple-500 text-white"
            data-testid="convert-story-btn"
          >
            {storyCost} &#11088;
          </DisButton>
        </div>
      </div>
    </DisCard>
  );
};

export default StarConversionSection;
